import type { StudioHumanizationComparison } from "../../../lib/studio/improvementHints";

function tone(delta: number) {
  if (delta > 0) {
    return {
      background: "rgba(44, 93, 67, 0.14)",
      color: "#214735",
    };
  }

  if (delta < 0) {
    return {
      background: "rgba(162, 62, 62, 0.12)",
      color: "#812d2d",
    };
  }

  return {
    background: "rgba(181, 141, 34, 0.16)",
    color: "#6d5210",
  };
}

function deltaLabel(value: number) {
  if (value > 0) return `+${value}`;
  return String(value);
}

function deltaPill(value: number) {
  const styles = tone(value);

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        padding: "0.28rem 0.62rem",
        background: styles.background,
        color: styles.color,
        fontSize: "0.74rem",
        fontWeight: 700,
      }}
    >
      {deltaLabel(value)}
    </span>
  );
}

export default function HumanizationComparisonPanel(props: {
  comparison: StudioHumanizationComparison;
}) {
  return (
    <section style={{ marginTop: "1.15rem" }}>
      <div
        style={{
          fontSize: "0.74rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "#7b726a",
          fontWeight: 700,
        }}
      >
        Tuning comparison
      </div>
      <h4 style={{ margin: "0.45rem 0 0", fontSize: "1.28rem", color: "#26221d" }}>
        Before and after comparison
      </h4>
      <p style={{ margin: "0.6rem 0 0", color: "#4d453d", lineHeight: 1.65 }}>
        {props.comparison.headline}
      </p>

      <div
        style={{
          marginTop: "0.95rem",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "0.85rem",
        }}
      >
        <section
          style={{
            borderRadius: 20,
            border: "1px solid rgba(54, 44, 35, 0.08)",
            background:
              "linear-gradient(180deg, rgba(251, 248, 243, 0.98), rgba(255, 255, 255, 0.98))",
            padding: "0.95rem",
          }}
        >
          <div
            style={{
              fontSize: "0.72rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#85796e",
              fontWeight: 700,
            }}
          >
            Before
          </div>
          <div style={{ marginTop: "0.38rem", color: "#26221d", fontWeight: 700 }}>
            {props.comparison.before.sourceLabel}
          </div>
          <p style={{ margin: "0.42rem 0 0", color: "#5b534b", lineHeight: 1.55 }}>
            {props.comparison.before.humanization.total.score}/
            {props.comparison.before.humanization.total.maxScore}
          </p>
        </section>

        <section
          style={{
            borderRadius: 20,
            border: "1px solid rgba(54, 44, 35, 0.08)",
            background:
              "linear-gradient(180deg, rgba(251, 248, 243, 0.98), rgba(255, 255, 255, 0.98))",
            padding: "0.95rem",
          }}
        >
          <div
            style={{
              fontSize: "0.72rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#85796e",
              fontWeight: 700,
            }}
          >
            After
          </div>
          <div style={{ marginTop: "0.38rem", color: "#26221d", fontWeight: 700 }}>
            {props.comparison.after.sourceLabel}
          </div>
          <p style={{ margin: "0.42rem 0 0", color: "#5b534b", lineHeight: 1.55 }}>
            {props.comparison.after.humanization.total.score}/
            {props.comparison.after.humanization.total.maxScore}
          </p>
        </section>

        <section
          style={{
            borderRadius: 20,
            border: "1px solid rgba(54, 44, 35, 0.08)",
            background:
              "linear-gradient(180deg, rgba(240, 248, 250, 0.9), rgba(248, 251, 252, 0.98))",
            padding: "0.95rem",
          }}
        >
          <div
            style={{
              fontSize: "0.72rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#5b7884",
              fontWeight: 700,
            }}
          >
            Overall delta
          </div>
          <div style={{ marginTop: "0.38rem" }}>{deltaPill(props.comparison.overallDelta)}</div>
          <p style={{ margin: "0.42rem 0 0", color: "#5b534b", lineHeight: 1.55 }}>
            The tuned draft remains review-only and does not change live routing.
          </p>
        </section>
      </div>

      <div style={{ marginTop: "0.95rem", display: "grid", gap: "0.75rem" }}>
        {props.comparison.dimensions.map((dimension) => (
          <section
            key={dimension.key}
            style={{
              borderRadius: 20,
              border: "1px solid rgba(54, 44, 35, 0.08)",
              background:
                "linear-gradient(180deg, rgba(251, 248, 243, 0.98), rgba(255, 255, 255, 0.98))",
              padding: "0.95rem",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1.2fr) repeat(3, minmax(64px, auto))",
                gap: "0.75rem",
                alignItems: "center",
              }}
            >
              <div>
                <div style={{ fontWeight: 700, color: "#26221d" }}>{dimension.label}</div>
                <p style={{ margin: "0.35rem 0 0", color: "#5b534b", lineHeight: 1.55 }}>
                  {dimension.detail}
                </p>
              </div>
              <div style={{ color: "#4d453d", fontWeight: 700, textAlign: "right" }}>
                {dimension.before}
              </div>
              <div style={{ color: "#4d453d", fontWeight: 700, textAlign: "right" }}>
                {dimension.after}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                {deltaPill(dimension.delta)}
              </div>
            </div>
          </section>
        ))}
      </div>
    </section>
  );
}
